import { useEffect, useState } from "react";
import { AnimatePresence, MotionConfig, motion } from "framer-motion";
import Lenis from "lenis";
import { Toaster } from "./components/ui/sonner";
import { Header } from "./components/Header";
import { Hero } from "./components/Hero";
import { Marquee } from "./components/Marquee";
import { BrandIntro } from "./components/BrandIntro";
import { MenuSection } from "./components/MenuSection";
import { CartDrawer } from "./components/CartDrawer";
import { Contact } from "./components/Contact";
import { Footer } from "./components/Footer";
import { DividerCurve } from "./components/Decor";
import { inr } from "./data/menu";

const loadCart = () => {
  try {
    return JSON.parse(localStorage.getItem("hk-cart")) || [];
  } catch {
    return [];
  }
};

function App() {
  const [cart, setCart] = useState(loadCart);
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("hk-cart", JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  const addToCart = (entry) =>
    setCart((prev) => {
      const i = prev.findIndex((x) => x.key === entry.key);
      if (i >= 0) {
        const next = [...prev];
        next[i] = { ...next[i], qty: next[i].qty + entry.qty };
        return next;
      }
      return [...prev, entry];
    });

  const updateQty = (key, delta) =>
    setCart((prev) => prev.map((x) => (x.key === key ? { ...x, qty: Math.max(1, x.qty + delta) } : x)));

  const removeItem = (key) => setCart((prev) => prev.filter((x) => x.key !== key));

  const subtotal = cart.reduce((s, x) => s + x.unitPrice * x.qty, 0);
  const count = cart.reduce((s, x) => s + x.qty, 0);

  return (
    <MotionConfig reducedMotion="user">
      <div className="min-h-screen bg-[#FFFDF8] text-[#5A4232] font-body antialiased">
        <Header count={count} subtotal={subtotal} onCartOpen={() => setCartOpen(true)} />
        <main>
          <Hero />
          <Marquee />
          <BrandIntro />
          <DividerCurve fill="#FAF4EC" flip />
          <MenuSection onAdd={addToCart} />
          <DividerCurve fill="#FAF4EC" />
          <Contact />
        </main>
        <Footer />
        <CartDrawer
          open={cartOpen}
          onClose={() => setCartOpen(false)}
          items={cart}
          onUpdateQty={updateQty}
          onRemove={removeItem}
          subtotal={subtotal}
          count={count}
        />
        <AnimatePresence>
          {count > 0 && !cartOpen && (
            <motion.button
              data-testid="mobile-cart-bar"
              initial={{ y: 90, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 90, opacity: 0 }}
              transition={{ type: "spring", damping: 26, stiffness: 300 }}
              onClick={() => setCartOpen(true)}
              className="fixed bottom-4 inset-x-4 z-40 md:hidden rounded-full bg-[#3D2314] text-[#FFFDF8] py-3.5 px-6 flex items-center justify-between shadow-[0_16px_40px_-10px_rgba(61,35,20,0.5)]"
            >
              <span className="text-sm font-semibold">
                {count} item{count > 1 ? "s" : ""} · {inr(subtotal)}
              </span>
              <span className="text-sm font-bold text-[#E8909D]">View Cart →</span>
            </motion.button>
          )}
        </AnimatePresence>
        <Toaster position="top-center" richColors />
      </div>
    </MotionConfig>
  );
}

export default App;
